import { useState } from 'react';
import { Dropdown, Input, Select, Spin } from 'antd';
import { LoadingOutlined, SyncOutlined } from '@ant-design/icons';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { BlueButton } from '@share/components';
import { RootState } from '@share/utils';
import { Currency } from '@components';
import { convertCurrency } from '@share/store/slices';
import { USD_CURRENCY } from '@constants';

import './style.scss';

interface IMapStateToProps {
  adminLevel: number;
  currencies: Array<{ code: string; symbol: string }>;
}

interface IMapDispatchToProps {
  convertCurrency: (
    payload: { FromCurrency: string; ToCurrency: string; Amount: number },
    sucessCallBack: (res: { conversionRate: number; convertedValue: number }) => void
  ) => void;
}

type Props = IMapStateToProps & IMapDispatchToProps & WrappedComponentProps;

function CurrencyCalculatorComponent(props: Props) {
  const { adminLevel, currencies, convertCurrency, intl } = props;
  const [openDropdown, setOpenDropdown] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [convertedAmountRate, setConvertedAmountRate] = useState<number | null>(null);
  const [fromCurrency, setFromCurrency] = useState<string | null>(USD_CURRENCY);
  const [toCurrency, setToCurrency] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (adminLevel <= 0) return null;

  const optionsFrom = currencies?.map((c) => ({ label: `${c.code} - ${c.symbol}`, value: c.code }));
  const optionsTo = currencies
    ?.filter((c) => c.code !== fromCurrency)
    ?.map((c) => ({ label: `${c.code} - ${c.symbol}`, value: c.code }));

  const handleCalculate = () => {
    if (!fromCurrency || !toCurrency || !amount) return;
     setLoading(true)
    convertCurrency(
      { FromCurrency: fromCurrency, ToCurrency: toCurrency, Amount:amount },
      (res) => {
        setConvertedAmount(res.convertedValue);
        setConvertedAmountRate(res.conversionRate);
        setLoading(false)
      }
    );
  };

  const items = [
    {
      key: '1',
      label: (
        <div className="currency-calculator__content">
          <div className="currency-calculator__input">
            <h5><FormattedMessage id="currency.calculator.title" /></h5>
          </div>

          <div className="currency-calculator__input">
            <Select
              value={fromCurrency || undefined}
              onChange={(val) => {
                setFromCurrency(val);
                setConvertedAmount(null);
                setConvertedAmountRate(null);
                if (val === toCurrency) setToCurrency(null);
              }}
              placeholder={intl.formatMessage({ id: 'currency.calculator.select.from' })}
              disabled={loading}
              options={optionsFrom}
            />
          </div>

          <div className="currency-calculator__input">
            <Select
              value={toCurrency || undefined}
              onChange={(val) => {
                setToCurrency(val);
                setConvertedAmount(null);
                setConvertedAmountRate(null);
              }}
              placeholder={intl.formatMessage({ id: 'currency.calculator.select.to' })}
              disabled={loading}
              options={optionsTo}
            />
          </div>

          <div className="currency-calculator__input">
            <Input
              type="number"
              disabled={loading}
              placeholder={intl.formatMessage({ id: 'currency.calculator.amount' })}
              value={amount ?? ''}
              onChange={(e) => setAmount(e.target.value ? +e.target.value : null)}
            />
          </div>

          <div className="currency-calculator__input">
            <BlueButton onClick={handleCalculate} disabled={!amount || !fromCurrency || !toCurrency || loading}>
              {loading ? <Spin indicator={<LoadingOutlined spin />} style={{ marginRight: 8 }} /> : null}
              {intl.formatMessage({ id: 'currency.calculator.calculate' })}
            </BlueButton>
          </div>

          <div className="currency-calculator__output">
            {convertedAmount != null ? (
              <>
                <span className="currency-calculator__output-text">
                  <label><FormattedMessage id="currency.calculator.converted.rate" /></label>
                  <label>{(Math.floor((convertedAmountRate || 0) * 1000000 ) / 1000000) || ''}</label>
                </span>
                <span className="currency-calculator__output-text bolded">
                  <label><FormattedMessage id="currency.calculator.amount" /></label>
                  <label>
                    {toCurrency && <Currency currency={toCurrency} />}{' '}
                    {(Math.floor((convertedAmount || 0) * 1000000 ) / 1000000) || ''}
                  </label>
                </span>
              </>
            ) : (
              <>
                <span style={{ color: 'red', width: '100%', textAlign: 'center' }}>
                  <FormattedMessage id="currency.calculator.select" />
                </span>
                <span style={{ marginTop: 10 }}>&nbsp;</span>
              </>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="currency-calculator">
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        open={openDropdown}
        overlayClassName="currency-calculator__dropdown"
        onOpenChange={(open) => setOpenDropdown(open)}
        placement="topRight"
      >
        <div className="currency-calculator__dropdown-icon" onClick={() => setOpenDropdown(!openDropdown)}>
          <SyncOutlined />
          <label>$</label>
        </div>
      </Dropdown>
    </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => ({
  adminLevel: state.navigationMenuStore?.items?.adminLevel || 0,
  currencies: state.currencyStore?.currencies || [],
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, Action>): IMapDispatchToProps => ({
  convertCurrency: (payload: { FromCurrency: string; ToCurrency: string; Amount: number }, sucessCallBack: (res: { conversionRate: number; convertedValue: number }) => void) => dispatch(convertCurrency({ from: payload.FromCurrency, to: payload.ToCurrency, amount: payload.Amount }, sucessCallBack)),
});

const IntlWrapped = injectIntl(CurrencyCalculatorComponent);
export const CurrencyCalculator = connect(mapStateToProps, mapDispatchToProps)(IntlWrapped);

